import type { ReactNode } from 'react';
import styled from '@emotion/styled';
import { Text } from '@components/Text/Text';
import type { DismissibleProps } from '@common/Dismissible';
import { CloseAction } from '@components/Actions/CloseAction/CloseAction';
import { radius, spacer } from '@tokens';
import type { ConsumedActionProps } from '../NotificationAction';
import { BORDER_COLORS, BACKGROUND_COLORS } from '../helpers';
import type { NotificationKind } from '../helpers';
import { NotificationAction } from '../NotificationAction';
import { NotificationIcon } from '../NotificationIcon';
import type { PropsOf } from '@emotion/react';
import { css } from '@emotion/react';
import { useStrings } from '@hooks/useStrings';
import { useInterpolate } from '@hooks/useInterpolate';

type AlertKind = NotificationKind;

export interface AlertProps<T extends boolean, L extends string | undefined>
  extends Omit<PropsOf<'div'>, 'title' | 'children'>,
    DismissibleProps<T>,
    ConsumedActionProps<L> {
  /**
   * Title of the alert.
   */
  title?: string;
  /**
   * Main content of the alert.
   */
  children: ReactNode;
  /**
   * Kind of alert.
   */
  kind?: AlertKind;
  /**
   * Direction (layout) of elements inside the alert.
   */
  direction?: 'row' | 'column';
  /**
   * Styles override.
   */
  className?: string;
}

const AlertContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'kind',
})<{ kind: AlertKind }>`
  width: 100%;
  background-color: ${({ kind }) => BACKGROUND_COLORS[kind]};
  border: 1px solid ${({ kind }) => BORDER_COLORS[kind]};
  border-radius: ${radius.l};
  min-height: ${spacer.x7};
  padding: ${spacer.x2} ${spacer.x3};
  display: flex;
  align-items: flex-start;
  gap: ${spacer.x2};
`;

const StyledIcon = styled(NotificationIcon)`
  margin-top: ${spacer.x0};
`;

const StyledAction = styled(NotificationAction)`
  flex-shrink: 0;
  text-align: left;
`;

const ContentContainer = styled.div<{ direction: 'row' | 'column' }>`
  flex-grow: 1;
  // specific margin to make sure the first line is well centered with the rest
  margin-top: 2px;
  display: flex;
  align-items: flex-start;
  flex-direction: ${({ direction }) => direction};
  gap: ${spacer.x2};
`;
const TextContainer = styled.div`
  flex-grow: 1;
`;

/**
 * Vibrant alert messages within pages or forms.
 *
 * {@link https://zeroheight.com/76c439891/p/9204f2-alert/b/89414b Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-alert--basic-alert Code documentation}
 *
 * @example
 * <Alert>My Alert</Alert>
 */
export const Alert = <T extends boolean, L extends string | undefined>({
  title,
  children,
  direction = 'row',
  kind = 'info',
  actionText,
  actionHref,
  onAction,
  isDismissible,
  onDismiss,
  actionProps,
  className,
  ...props
}: AlertProps<T, L>): JSX.Element => {
  const { alert } = useStrings();
  const interpolate = useInterpolate();

  return (
    <AlertContainer
      {...props}
      className={className}
      kind={kind}
      data-testid={`alert-${kind}`}
    >
      <StyledIcon kind={kind} />
      <ContentContainer direction={direction}>
        <TextContainer>
          {title && (
            <Text
              textType="subtitle2"
              css={css`
                display: block;
                margin-bottom: ${spacer.x0};
              `}
            >
              {title}
            </Text>
          )}
          <Text
            as="p"
            css={css`
              margin-bottom: 0;
            `}
          >
            {children}
          </Text>
        </TextContainer>

        {actionText && (
          <StyledAction
            {...actionProps}
            kind="standalone"
            href={actionHref}
            onClick={onAction}
          >
            {actionText}
          </StyledAction>
        )}
      </ContentContainer>

      {isDismissible && (
        <CloseAction
          onClick={onDismiss}
          description={interpolate(alert.closeActionDescription, { kind })}
          className={`alert-${kind}-close`}
        />
      )}
    </AlertContainer>
  );
};
